import { SettingsData, TimeSlot } from "../types/types";

interface TimeSlotsByHour {
  [hour: string]: TimeSlot[];
}

const AvailableTimes: React.FC<{
  settings: SettingsData;
  selectedDate: string;
  loading: boolean;
  availableSlots: TimeSlot[];
  selectedSlots: string[];
  getDayOfWeek: (date: string) => keyof SettingsData['schedule'];
  handleSlotSelect: (slotId: string) => void;
  availableItems: string; // "availableKarts" | "availableSimulators" | "availablePlayhouses" | "availableVr"
  onInvalidTimeSelect: () => void;
}> = ({ settings, selectedDate, loading, availableSlots, selectedSlots, getDayOfWeek, handleSlotSelect, availableItems, onInvalidTimeSelect }) => {
  // Időpontok csoportosítása órák szerint
  const groupedTimeSlots = availableSlots.reduce((acc: TimeSlotsByHour, slot) => {
    const hour = slot.startTime.split(':')[0];
    if (!acc[hour]) {
      acc[hour] = [];
    }
    acc[hour].push(slot);
    return acc;
  }, {});

  const maxItems = settings[availableItems as keyof SettingsData] as number;

  // Aktuális dátum és idő ellenőrzése
  const isSlotAvailable = (slot: TimeSlot) => {
    const now = new Date();
    const [hours, minutes] = slot.startTime.split(':').map(Number);

    // A kiválasztott időpont teljes dátummal és idővel
    const slotDateTime = new Date(selectedDate);
    slotDateTime.setHours(hours, minutes, 0, 0);

    // A jelenlegi időpont 5 perc ráhagyással
    const nowPlus5Min = new Date(now);
    nowPlus5Min.setMinutes(now.getMinutes() + 5);

    // Összehasonlítjuk a teljes dátumot és időt
    return slotDateTime > nowPlus5Min;
  };

  const handleTimeSelect = (slot: TimeSlot) => {
    if (!isSlotAvailable(slot)) {
      onInvalidTimeSelect();
      return;
    }
    handleSlotSelect(slot.id);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div id="timeSlotSection" className="mt-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Válassz időpontot:</h3>

      {/* Zártkörű események megjelenítése */}
      {settings.privateBlocks
        .filter(block => {
          if (!block.date) return false;
          try {
            const blockDate = new Date(block.date);
            if (isNaN(blockDate.getTime())) return false;
            return blockDate.toISOString().split('T')[0] === selectedDate &&
              block.serviceType === 'vr';
          } catch (error) {
            console.error("Invalid date in privateBlocks:", block.date);
            return false;
          }
        })
        .length > 0 && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800 font-medium">Zártkörű VR eszköz események ezen a napon:</p>
            <ul className="mt-2 space-y-1">
              {settings.privateBlocks
                .filter(block => {
                  if (!block.date) return false;
                  try {
                    const blockDate = new Date(block.date);
                    if (isNaN(blockDate.getTime())) return false;
                    return blockDate.toISOString().split('T')[0] === selectedDate &&
                      block.serviceType === 'vr';
                  } catch (error) {
                    console.error("Invalid date in privateBlocks:", block.date);
                    return false;
                  }
                })
                .map((block, index) => (
                  <li key={index} className="text-sm text-yellow-700">
                    {block.startHour}:00 - {block.endHour}:00
                    {block.description && ` (${block.description})`}
                  </li>
                ))}
            </ul>
          </div>
        )}

      {/* Meglévő időpont választó megjelenítése */}
      {!settings.schedule[getDayOfWeek(selectedDate) as keyof SettingsData['schedule']].isOpen && (
        <div className="text-center py-4">
          <p className="text-red-600">A kiválasztott napon zárva vagyunk.</p>
          <p className="text-gray-600 mt-2">Kérjük, válassz másik napot!</p>
        </div>
      )}

      {Object.entries(groupedTimeSlots).map(([hour, slots]) => (
        <div key={hour} className="mb-6">
          <h4 className="text-md font-medium text-gray-700 mb-2">{hour}:00</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {slots.map((slot) => (
              <button
                key={slot.id}
                onClick={() => handleTimeSelect(slot)}
                disabled={!slot.available || !isSlotAvailable(slot)}
                className={`
                  relative p-2 rounded-md text-sm font-medium shadow-sm
                  ${!isSlotAvailable(slot)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                    : !slot.available
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : slot.isPrivateTime
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }
                  ${selectedSlots.includes(slot.id) && 'ring-2 ring-green-500'}
                  transition-all duration-200
                `}
              >
                <div>{slot.startTime}</div>
                {typeof slot.availableCount === 'number' && (
                  <div className="text-xs text-gray-500 mt-1">
                    {slot.availableCount} / {maxItems} db hely
                  </div>
                )}
                {slot.isPrivateTime && (
                  <div className="text-xs text-gray-500 mt-1">
                    Zártkörű esemény
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      ))}

      {availableSlots.length === 0 && (
        <div className="text-center py-4 text-gray-500">
          Nincsenek elérhető időpontok ezen a napon.
        </div>
      )}
    </div>
  );
};

export default AvailableTimes;
